/**
 * O orçamento do IW no Caso (`TP-066`, T6): `GET /casos/{id}/orcamento-iw` e a conferência
 * `POST /casos/{id}/orcamento-iw/avaliacoes`. A seção `*Api` espelha
 * `contracts/openapi/anery-api.v1.json` (schemas `OrcamentoDoCasoResponse`,
 * `AvaliacaoDoOrcamentoDoIwResponse`), escrita à mão como em `tipos.ts`. Os valores de
 * enum são o nome do membro C#.
 */

import { requisitar } from '../../../shared/api/clienteHttp';
import { mapearAvaliacao } from './tipos';
import type { Avaliacao, AvaliacaoApi, ModalidadeDoMotorApi } from './tipos';

export type ResultadoDoOrcamentoApi = 'Encontrado' | 'SemCarteira' | 'SemContratoNoIw' | 'NaoEncontrado' | 'Ambiguo';

export type AvisoDoItemIwApi = 'SemCodigo' | 'SemUnidade' | 'QuantidadeNaoPositiva' | 'ValorNegativo';

/** O motivo de o item ficar fora da conferência: o motor reprovaria o pedido inteiro por ele. */
export const ROTULOS_DO_AVISO: Record<AvisoDoItemIwApi, string> = {
  SemCodigo: 'sem código',
  SemUnidade: 'sem unidade',
  QuantidadeNaoPositiva: 'quantidade zero ou negativa',
  ValorNegativo: 'valor negativo',
};

export interface ItemDoOrcamentoIwApi {
  readonly grupo: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly quantidade: number;
  readonly quantidadePacote: number | null;
  readonly unidade: string;
  readonly valorUnitario: number;
  readonly descontoPercentual: number;
  readonly valorComDesconto: number;
  readonly valorTotal: number;
  readonly aviso: AvisoDoItemIwApi | null;
  /** O código do item no catálogo do IW (`NR-104`); ausente enquanto o IW não o mandar. */
  readonly codigoScc?: string | null;
}

export interface OrcamentoDoCasoApi {
  readonly resultado: ResultadoDoOrcamentoApi;
  readonly inicio: string;
  readonly fim: string;
  readonly candidatos: number;
  readonly contrato: { readonly id: string; readonly nome: string; readonly iwContratoId: number } | null;
  readonly orcamento: {
    readonly admissionId: number;
    readonly budgetId: number;
    readonly inicio: string | null;
    readonly fim: string | null;
    readonly codigoAutorizacao: string | null;
    readonly valorTotalPeriodo: number;
    readonly nomePaciente: string | null;
    readonly itens: readonly ItemDoOrcamentoIwApi[];
  } | null;
}

interface AvaliacaoDoOrcamentoDoIwApi {
  readonly avaliacao: AvaliacaoApi;
  readonly budgetId: number;
  readonly foraDaConferencia: number;
}

export function chaveDoOrcamentoDoIw(casoId: string, competencia: string, rodada: number): readonly unknown[] {
  return ['casos', casoId, 'orcamento-iw', competencia, rodada];
}

/** `atualizar` ignora o cache de 5 min da API: é o "buscar de novo no IW". */
export function obterOrcamentoDoIw(casoId: string, competencia: string, atualizar: boolean, sinal?: AbortSignal): Promise<OrcamentoDoCasoApi> {
  const consulta = new URLSearchParams({ competencia });
  if (atualizar) consulta.set('atualizar', 'true');
  return requisitar<OrcamentoDoCasoApi>(`/casos/${encodeURIComponent(casoId)}/orcamento-iw?${consulta.toString()}`, { sinal });
}

export async function conferirOrcamentoDoIw(
  casoId: string,
  competencia: string,
  modalidade: ModalidadeDoMotorApi,
): Promise<{ avaliacao: Avaliacao; foraDaConferencia: number }> {
  const dado = await requisitar<AvaliacaoDoOrcamentoDoIwApi>(
    `/casos/${encodeURIComponent(casoId)}/orcamento-iw/avaliacoes?${new URLSearchParams({ competencia }).toString()}`,
    { method: 'POST', corpo: { modalidade } },
  );
  return { avaliacao: mapearAvaliacao(dado.avaliacao), foraDaConferencia: dado.foraDaConferencia };
}

/** `K_CAP_APPROVAL_STATUS` do IW, pelo nome do membro C# (`IwApprovalStatus`). */
export type StatusDeAprovacaoNoIwApi = 'NotRequired' | 'Awaiting' | 'Approved' | 'Rejected' | 'Partial';

/** Os rótulos do IW para o status (`IFRCONSTANTITEM`). */
export const ROTULOS_DO_STATUS_NO_IW: Record<StatusDeAprovacaoNoIwApi, string> = {
  NotRequired: 'Não necessita aprovação',
  Awaiting: 'Aguardando aprovação',
  Approved: 'Aprovado',
  Rejected: 'Reprovado',
  Partial: 'Aprovação parcial',
};

/** `GET /casos/{id}/orcamento-iw/decisao` (`TP-068`): o status interno no IW e se a decisão pode sair. */
export interface SituacaoDaDecisaoApi {
  readonly orcamento: string;
  readonly statusInterno: StatusDeAprovacaoNoIwApi | null;
  readonly cancelado: boolean;
  readonly atualizadoEm: string | null;
  readonly alcadaRegistrada: boolean;
  readonly ligada: boolean;
  /** A versão lida do IW: volta no clique, e a decisão só vale sobre ela. */
  readonly versao: string | null;
  /** Quem está na sessão pode decidir (a mesma capacidade de enviar o orçamento à operadora). */
  readonly podeDecidir: boolean;
}

export type DecisaoDoOrcamentoApi = 'Approve' | 'Reject';

export type ResultadoDaDecisaoApi = 'Recorded' | 'Pending' | 'Refused' | 'Unanswered';

/** `POST /casos/{id}/orcamento-iw/decisao` (`TP-068`). */
export interface RespostaDaDecisaoApi {
  readonly resultado: ResultadoDaDecisaoApi;
  readonly camposRecusados: readonly { readonly field: string; readonly message: string }[];
  readonly motivo: string | null;
  readonly recusadoPelaPlataforma: boolean;
}

export function chaveDaDecisaoDoOrcamento(casoId: string, competencia: string, orcamento: number): readonly unknown[] {
  return ['casos', casoId, 'orcamento-iw', competencia, 'decisao', orcamento];
}

export function obterSituacaoDaDecisao(
  casoId: string,
  competencia: string,
  orcamento: number,
  sinal?: AbortSignal,
): Promise<SituacaoDaDecisaoApi> {
  const consulta = new URLSearchParams({ competencia, orcamento: String(orcamento) });
  return requisitar<SituacaoDaDecisaoApi>(`/casos/${encodeURIComponent(casoId)}/orcamento-iw/decisao?${consulta.toString()}`, { sinal });
}

export function decidirOrcamentoNoIw(
  casoId: string,
  competencia: string,
  orcamento: number,
  versao: string,
  decisao: DecisaoDoOrcamentoApi,
): Promise<RespostaDaDecisaoApi> {
  return requisitar<RespostaDaDecisaoApi>(`/casos/${encodeURIComponent(casoId)}/orcamento-iw/decisao`, {
    method: 'POST',
    corpo: { competencia, orcamento: String(orcamento), versao, decisao },
  });
}
